import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import JsonLd from "@/components/JsonLd";
import SeoFooter from "@/components/SeoFooter";
import { findService, ORIGIN } from "@/data/seoPages";

const GOLD = "#C7A668";

export default function ServicePage({ lang: routeLang }) {
  const ctx = useLang();
  const lang = routeLang || ctx.lang;
  const L = (en, es) => (lang === "en" ? en : es);
  const { slug } = useParams();
  const svc = findService(slug, lang);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!svc) return <Navigate to={lang === "en" ? "/en" : "/"} replace />;

  const c = svc[lang];
  const esPath = `/servicios/${svc.esSlug}`;
  const enPath = `/en/services/${svc.enSlug}`;
  const canonical = ORIGIN + (lang === "en" ? enPath : esPath);
  const bookPath = lang === "en" ? "/en/book" : "/book";

  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: c.h1,
        serviceType: lang === "en" ? "Aircraft detailing and cabin care" : "Detailing y cabin care de aeronaves",
        description: c.desc,
        provider: { "@type": "ProfessionalService", name: "dukk", url: ORIGIN },
        areaServed: { "@type": "Country", name: "Mexico" },
        audience: { "@type": "Audience", audienceType: lang === "en" ? "Aircraft owners, pilots, aircraft managers, FBOs, family offices and fleet operators" : "Dueños de aeronaves, pilotos, administradores, FBOs, family offices y operadores de flota" },
        url: canonical,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: L("Home", "Inicio"), item: ORIGIN + (lang === "en" ? "/en" : "/") },
          { "@type": "ListItem", position: 2, name: c.h1, item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="grain" style={{ minHeight: "100vh", paddingTop: 110 }}>
      <PageSeo esPath={esPath} enPath={enPath} title={c.title} description={c.desc} lang={lang} image={`${ORIGIN}/og/svc-${svc.key}-${lang}.png`} />
      <JsonLd data={jsonld} id="jsonld-service" />
      <article data-testid="service-page" style={{ maxWidth: 880, margin: "0 auto", padding: "0 clamp(20px,5vw,40px)" }}>
        <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".18em", color: GOLD, marginBottom: 14 }}>{c.kicker.toUpperCase()}</div>
        <h1 style={{ fontSize: "clamp(30px,5vw,52px)", fontWeight: 400, margin: 0, letterSpacing: "-.02em", lineHeight: 1.08 }}>{c.h1}</h1>
        <p style={{ fontSize: "clamp(16px,2vw,19px)", color: "#B8BDC3", lineHeight: 1.65, margin: "22px 0 0", maxWidth: 720 }}>{c.intro}</p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "30px 0 8px" }}>
          <Link to={bookPath} data-testid="service-cta-book" className="tb-cta" style={{ background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 4, padding: "12px 22px", fontSize: 14, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>{L("Book service", "Reservar servicio")}<ArrowRight size={15} /></Link>
          <Link to={lang === "en" ? "/en/membership" : "/membership"} data-testid="service-cta-membership" style={{ color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 4, padding: "12px 22px", fontSize: 14, textDecoration: "none" }}>{L("Membership", "Membresía")}</Link>
        </div>

        {c.sections.map((s, i) => (
          <section key={i} style={{ marginTop: 40 }}>
            <h2 style={{ fontSize: "clamp(20px,2.6vw,26px)", fontWeight: 400, margin: "0 0 12px", letterSpacing: "-.01em" }}>{s.h}</h2>
            <p style={{ fontSize: 16, color: "#B8BDC3", lineHeight: 1.7, margin: 0, maxWidth: 720 }}>{s.body}</p>
          </section>
        ))}

        <section style={{ marginTop: 40 }}>
          <h2 style={{ fontSize: "clamp(20px,2.6vw,26px)", fontWeight: 400, margin: "0 0 16px", letterSpacing: "-.01em" }}>{L("Highlights", "Lo esencial")}</h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 11 }}>
            {c.bullets.map((b, i) => (
              <li key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start", fontSize: 15.5, color: "#d7dadd" }}><Check size={16} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />{b}</li>
            ))}
          </ul>
        </section>

        <div style={{ marginTop: 44, padding: "26px clamp(20px,3vw,32px)", border: "1px solid rgba(199,166,104,.3)", background: "rgba(199,166,104,.05)", borderRadius: 8 }}>
          <p style={{ fontSize: 16, color: "#E7E4DD", margin: "0 0 16px", lineHeight: 1.6 }}>{L("Ready to get your aircraft departure-ready?", "¿Listo para dejar tu aeronave lista para salir?")}</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to={bookPath} className="tb-cta" style={{ background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 4, padding: "11px 20px", fontSize: 14, textDecoration: "none" }}>{L("Book service", "Reservar servicio")}</Link>
            <Link to={lang === "en" ? "/en/priority" : "/priority"} style={{ color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 4, padding: "11px 20px", fontSize: 14, textDecoration: "none" }}>{L("Priority request", "Solicitud prioritaria")}</Link>
          </div>
        </div>
      </article>
      <SeoFooter />
    </div>
  );
}
